import { enumLabel, t } from '@ctcells/i18n'
import { useId, useRef, useState } from 'react'

import { Button } from '../../atoms/Button.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { Radio } from '../../atoms/Radio.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { TextInput } from '../../atoms/TextInput.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import './dispositionValidation.css'
import type { ActionGate } from '../access.ts'

import {
  dispositionRequests,
  rerunBlocked,
  type DispositionCandidate,
  type DispositionChoice,
  type DispositionOutcome,
} from './dispositionModel.ts'

/**
 * **복구 › 시료 처분** 창 — 중단된 작업의 종착 아닌 시료마다 [다시 돌림] · [폐기]를 고른다(정본 07 §4.4 [A-3] B-0252 ·
 * 코어 관리자 페이지 `recover_form.js` `dispositionPanel` 과 같은 길).
 *
 * - 시료마다 하나를 다 골라야 · 사유를 적어야 [처분 확정]이 켜진다.
 * - 보내면 결정마다 요청 하나(`discard_samples` · `rerun_samples` · 사유 공통). 처리된 줄은 «처리됨»으로 접고 다음 보내기에서 뺀다.
 * - 서버가 받지 않은 줄(422 `context.sample_ids`)은 그 줄에 사유가 선다 — 그 요청은 아무것도 바꾸지 않았다(계약).
 * - 남은 줄이 없으면 닫는다. 🔴 화면이 처분 대기를 풀지 않는다 — 서버가 `review_reason` 을 내리면 복구 줄이 사라진다.
 * 🔴 권한 · 제어락 · 서버 `allowed_commands`(`recover`)는 부르는 쪽이 `gate` 로 넘긴다(숨기지 않고 비활성 + 사유).
 */
export function DispositionModal({
  batchId,
  candidates,
  notice,
  onSubmit,
  onClose,
  disabled,
  disabledReason,
}: {
  batchId: string
  candidates: readonly DispositionCandidate[]
  /** 시료 목록을 못 받은 사유(viewer 403 · 실패) — 있으면 표 대신 선다 */
  notice?: string | undefined
  onSubmit: (
    requests: Record<DispositionChoice, string[]>,
    reason: string,
  ) => Promise<DispositionOutcome>
  onClose: () => void
} & ActionGate) {
  const [choices, setChoices] = useState<ReadonlyMap<string, DispositionChoice>>(new Map())
  const [reason, setReason] = useState('')
  const [done, setDone] = useState<ReadonlySet<string>>(new Set())
  const [refused, setRefused] = useState<ReadonlySet<string>>(new Set())
  const [errors, setErrors] = useState<readonly string[]>([])
  const [sending, setSending] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const nameBase = useId()

  const pending = candidates.filter((candidate) => !done.has(candidate.sampleId))
  const requests = dispositionRequests(pending, choices)
  const validation = useSubmitValidation<
    { reason: string; choices: ReadonlyMap<string, DispositionChoice> },
    string
  >((value) => ({
    ...Object.fromEntries(
      pending
        .filter((c) => !value.choices.has(c.sampleId))
        .map((c) => [c.sampleId, t('validation.choiceRequired')]),
    ),
    reason: value.reason.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  /*
   * 🔴 **시료 목록을 못 봤거나(조회 실패 · viewer) 후보가 0건이면 보내지 않는다** — 표가 사라져도 앞서 고른 것(`choices`)과 사유는
   *    남아 있어서, 재조회가 실패하면(쿼리가 옛 자료를 쥔 채 `isError`) 보이지 않는 선택으로 폐기가 나갈 수 있었다. 화면에 보이는
   *    줄로만 보낸다 — 사유는 표 자리에 선 문구 그대로다.
   */
  const gateProps: ActionGate = disabled
    ? { disabled: true, disabledReason }
    : notice !== undefined
      ? { disabled: true, disabledReason: notice }
      : candidates.length === 0
        ? { disabled: true, disabledReason: t('disposition.empty') }
        : sending
          ? { disabled: true, disabledReason: t('reason.actionInFlight') }
          : pending.length === 0
            ? { disabled: true, disabledReason: t('disposition.done') }
            : { disabled: false }

  const pick = (sampleId: string, choice: DispositionChoice) => {
    if (done.has(sampleId)) return
    const next = new Map(choices).set(sampleId, choice)
    setChoices(next)
    validation.change(sampleId, { reason, choices: next })
  }
  const pickAll = (choice: DispositionChoice) => {
    const next = new Map(choices)
    for (const candidate of pending)
      if (choice === 'discard_samples' || !rerunBlocked(candidate))
        next.set(candidate.sampleId, choice)
    setChoices(next)
    for (const candidate of pending)
      validation.change(candidate.sampleId, { reason, choices: next })
  }

  const submit = () => {
    if (
      gateProps.disabled ||
      !validation.validate({ reason, choices }, root.current) ||
      requests === null
    )
      return
    setSending(true)
    void onSubmit(requests, reason.trim()).then(
      (outcome) => {
        setSending(false)
        const nextDone = new Set([...done, ...outcome.done])
        setDone(nextDone)
        setRefused(new Set(outcome.refused))
        setErrors(outcome.errors)
        if (candidates.every((candidate) => nextDone.has(candidate.sampleId))) onClose()
      },
      () => setSending(false),
    )
  }

  const noteOf = (candidate: DispositionCandidate): string => {
    if (done.has(candidate.sampleId)) return t('disposition.done')
    if (refused.has(candidate.sampleId)) return t('disposition.refused')
    if (rerunBlocked(candidate)) return t('disposition.rerunBlocked')
    return ''
  }

  return (
    <Modal
      footer={
        <>
          <Button onClick={onClose} size="pill">
            {t('action.cancel')}
          </Button>
          <Button onClick={submit} size="pill" variant="primary" {...gateProps}>
            {t('disposition.submit')}
          </Button>
        </>
      }
      onClose={onClose}
      /* 시료 줄 표(웰 · 시료 번호 · 단계 · 처분 둘 · 비고)가 기본 창(560px)을 넘는다 — 시약 확인 창과 같은 넓은 창 */
      size="lg"
      title={`${t('action.recover')} › ${t('disposition.title')}`}
    >
      <div ref={root}>
        <ValueRows rows={[{ key: 'batch', label: t('column.batch'), value: batchId }]} />
        {/* 두 갈래의 뜻 — 계약 `RecoverDecision.sample_ids` · `reason` 설명 그대로(지어내지 않는다) */}
        <ul className="disposition-help">
          <li>{t('disposition.help.rerun')}</li>
          <li>{t('disposition.help.discard')}</li>
          <li>{t('disposition.help.reason')}</li>
        </ul>
        {notice !== undefined ? (
          <p className="vissue" role="alert">
            {notice}
          </p>
        ) : candidates.length === 0 ? (
          <p>{t('disposition.empty')}</p>
        ) : (
          <>
            <div className="btnrow">
              <Button onClick={() => pickAll('rerun_samples')} size="pill">
                {t('disposition.allRerun')}
              </Button>
              <Button onClick={() => pickAll('discard_samples')} size="pill">
                {t('disposition.allDiscard')}
              </Button>
            </div>
            <table className="a-tbl a-tbl--managed a-tbl--even disposition-table">
              <caption className="a-sr">{t('disposition.title')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('column.well')}</th>
                  <th scope="col">{t('column.sampleId')}</th>
                  <th scope="col">{t('column.stage')}</th>
                  <th scope="col">{t('disposition.choice')}</th>
                  <th scope="col">{t('disposition.note')}</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => {
                  const id = candidate.sampleId
                  const choice = choices.get(id)
                  const settled = done.has(id)
                  const name = `${nameBase}-${id}`
                  return (
                    <tr data-sample={id} key={id}>
                      <td className="mono">{candidate.well ?? '—'}</td>
                      <td className="mono">{id}</td>
                      <td>
                        {enumLabel('enum.sampleStage', candidate.stage)}
                        {candidate.status === 'awaiting_decision'
                          ? ` · ${enumLabel('enum.sampleStatus', candidate.status)}`
                          : ''}
                      </td>
                      <td>
                        <div
                          className="disposition-choice"
                          id={validation.field(id).inputProps.id}
                          aria-invalid={validation.field(id).inputProps.invalid}
                          aria-describedby={validation.field(id).inputProps.describedBy}
                        >
                          <Radio
                            checked={choice === 'rerun_samples'}
                            label={t('disposition.rerun')}
                            name={name}
                            onChange={() => pick(id, 'rerun_samples')}
                            value="rerun_samples"
                            {...(settled
                              ? { disabled: true, disabledReason: t('disposition.done') }
                              : rerunBlocked(candidate)
                                ? { disabled: true, disabledReason: t('disposition.rerunBlocked') }
                                : {})}
                          />
                          <Radio
                            checked={choice === 'discard_samples'}
                            label={t('disposition.discard')}
                            name={name}
                            onChange={() => pick(id, 'discard_samples')}
                            value="discard_samples"
                            {...(settled
                              ? { disabled: true, disabledReason: t('disposition.done') }
                              : {})}
                          />
                        </div>
                        {validation.errors[id] ? (
                          <p
                            className="a-field__error"
                            role="alert"
                            id={validation.field(id).fieldProps.errorId}
                          >
                            {validation.errors[id]}
                          </p>
                        ) : null}
                      </td>
                      <td data-note={id}>{noteOf(candidate)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <Field {...reasonField.fieldProps} label={t('field.reason')}>
              <TextInput
                {...reasonField.inputProps}
                onChange={(next) => {
                  setReason(next)
                  validation.change('reason', { reason: next, choices })
                }}
                value={reason}
                wide
              />
            </Field>
          </>
        )}
        {errors.length === 0 ? null : (
          <p className="vissue" role="alert">
            {errors.join(' · ')}
          </p>
        )}
      </div>
    </Modal>
  )
}
